import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Form, FormControl, FormField, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/useAuth";
import { useEtablissement } from "@/contexts/EtablissementContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

const TYPES_STRUCTURE = [
  'EAJE', 'Crèche', 'MECS', 'SAAD', 'IME', 'ITEP', 'SESSAD',
  'CAMSP', 'EHPAD', 'Résidence autonomie', 'SSIAD', 'MAS', 'FAM', 'ESAT', 'Autre'
] as const;

const etablissementSchema = z.object({
  name: z.string().min(1, "Le nom officiel est obligatoire"),
  numero_finess: z.string().optional(),
  numero_siret: z.string().optional(),
  type_structure: z.string().min(1, "Le type de structure est obligatoire"),
  adresse: z.string().min(1, "L'adresse est obligatoire"),
  code_postal: z.string().min(1, "Le code postal est obligatoire"),
  ville: z.string().min(1, "La ville est obligatoire"),
  telephone_etablissement: z.string().optional(),
  nom_responsable_legal: z.string().min(1, "Le nom du responsable légal est obligatoire"),
  qualite_responsable_legal: z.string().min(1, "La qualité du responsable légal est obligatoire"),
});

type EtablissementFormData = z.infer<typeof etablissementSchema>;

export function EtablissementSettings() {
  const { profile } = useAuth();
  const { etablissement } = useEtablissement();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = React.useState(false);
  const etablissementId = etablissement?.id;
  const orgId = profile?.organization_id;

  const form = useForm<EtablissementFormData>({
    resolver: zodResolver(etablissementSchema),
    defaultValues: {
      name: '',
      numero_finess: '',
      numero_siret: '',
      type_structure: '',
      adresse: '',
      code_postal: '',
      ville: '',
      telephone_etablissement: '',
      nom_responsable_legal: '',
      qualite_responsable_legal: '',
    },
  });

  const initializedRef = React.useRef(false);

  // Pré-remplir le formulaire depuis l'établissement actif — une seule fois
  React.useEffect(() => {
    if (etablissement && !initializedRef.current) {
      form.reset({
        name: etablissement.nom || '',
        numero_finess: etablissement.numero_finess || '',
        numero_siret: etablissement.numero_siret || '',
        type_structure: etablissement.type_structure || '',
        adresse: etablissement.adresse || '',
        code_postal: etablissement.code_postal || '',
        ville: etablissement.ville || '',
        telephone_etablissement: etablissement.telephone || '',
        nom_responsable_legal: etablissement.nom_responsable || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim(),
        qualite_responsable_legal: etablissement.qualite_responsable || '',
      });
      initializedRef.current = true;
    }
  }, [etablissement, profile, form]);

  const onSubmit = async (data: EtablissementFormData) => {
    if (!etablissementId) return;
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('etablissements')
        .update({
          nom: data.name,
          numero_finess: data.numero_finess || null,
          numero_siret: data.numero_siret || null,
          type_structure: data.type_structure,
          adresse: data.adresse,
          code_postal: data.code_postal,
          ville: data.ville,
          telephone: data.telephone_etablissement || null,
          nom_responsable: data.nom_responsable_legal,
          qualite_responsable: data.qualite_responsable_legal,
          updated_at: new Date().toISOString(),
        })
        .eq('id', etablissementId);

      if (error) throw error;

      // Invalider le cache
      queryClient.invalidateQueries({ queryKey: ['etablissements'] });

      toast({
        title: "Succès",
        description: "Informations de l'établissement mises à jour.",
      });
    } catch (err: any) {
      toast({
        title: "Erreur",
        description: err.message || "Impossible de mettre à jour les informations.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const inputClassName = "w-full px-3 py-4 h-12 bg-white rounded-lg border border-gray-300 text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] focus:border-teal-400 focus:ring-1 focus:ring-teal-400 focus:outline-none";
  const labelClassName = "text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']";

  return (
    <div className="w-full inline-flex flex-col justify-start items-start gap-6">
      <div className="self-stretch flex flex-col justify-start items-start gap-1">
        <div className="self-stretch justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">
          Mon établissement
        </div>
        <div className="justify-center text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">
          Ces informations apparaissent dans le rapport de conformité officiel
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-5">
          {/* Nom officiel - 100% */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <div className="w-full flex flex-col gap-2">
                <FormLabel className={labelClassName}>
                  Nom officiel de l'établissement *
                </FormLabel>
                <FormControl>
                  <Input placeholder="Crèche Les Bambins" {...field} className={inputClassName} />
                </FormControl>
                <FormMessage />
              </div>
            )}
          />

          {/* FINESS + SIRET - 2 colonnes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="numero_finess"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className={labelClassName}>
                    Numéro FINESS
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="750051234" {...field} className={inputClassName} />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="numero_siret"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className={labelClassName}>
                    Numéro SIRET
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="123 456 789 00012" {...field} className={inputClassName} />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />
          </div>

          {/* Type de structure - Select */}
          <FormField
            control={form.control}
            name="type_structure"
            render={({ field }) => (
              <div className="w-full flex flex-col gap-2">
                <FormLabel className={labelClassName}>
                  Type de structure *
                </FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className={inputClassName}>
                      <SelectValue placeholder="Sélectionner le type de structure" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {TYPES_STRUCTURE.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </div>
            )}
          />

          {/* Adresse - 100% */}
          <FormField
            control={form.control}
            name="adresse"
            render={({ field }) => (
              <div className="w-full flex flex-col gap-2">
                <FormLabel className={labelClassName}>
                  Adresse *
                </FormLabel>
                <FormControl>
                  <Input placeholder="12 rue des Lilas" {...field} className={inputClassName} />
                </FormControl>
                <FormMessage />
              </div>
            )}
          />

          {/* Code postal + Ville - 2 colonnes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="code_postal"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className={labelClassName}>
                    Code postal *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="75011" {...field} className={inputClassName} />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="ville"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className={labelClassName}>
                    Ville *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Paris" {...field} className={inputClassName} />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />
          </div>

          {/* Téléphone - 100% */}
          <FormField
            control={form.control}
            name="telephone_etablissement"
            render={({ field }) => (
              <div className="w-full flex flex-col gap-2">
                <FormLabel className={labelClassName}>
                  Téléphone
                </FormLabel>
                <FormControl>
                  <Input placeholder="01 23 45 67 89" {...field} className={inputClassName} />
                </FormControl>
                <FormMessage />
              </div>
            )}
          />

          {/* Responsable légal + Qualité - 2 colonnes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="nom_responsable_legal"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className={labelClassName}>
                    Nom du responsable légal *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Marie Laurent" {...field} className={inputClassName} />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />

            <FormField
              control={form.control}
              name="qualite_responsable_legal"
              render={({ field }) => (
                <div className="w-full flex flex-col gap-2">
                  <FormLabel className={labelClassName}>
                    Qualité du responsable légal *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Directrice" {...field} className={inputClassName} />
                  </FormControl>
                  <FormMessage />
                </div>
              )}
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading || !form.formState.isDirty}
            className="h-11 px-4 py-3 bg-teal-400 rounded-lg text-white font-semibold font-['Bricolage_Grotesque'] hover:bg-teal-500 border-0"
          >
            {isLoading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </form>
      </Form>
    </div>
  );
}
